<template>
  <div v-if="isActive" class="search-tour">
    <!-- Backdrop -->
    <div class="tour-backdrop" @click="handleBackdropClick"></div>

    <!-- Highlight overlay -->
    <div
      v-if="currentStep && currentStep.target"
      class="tour-highlight"
      :style="highlightStyle"
    ></div>

    <!-- Tour tooltip -->
    <div
      v-if="currentStep"
      class="tour-tooltip"
      :style="tooltipStyle"
      :class="tooltipClass"
    >
      <div class="tooltip-header">
        <h4 v-if="currentStep.title" class="tooltip-title">{{ currentStep.title }}</h4>
        <button class="close-button" @click="closeTour">
          <i class="el-icon-close"></i>
        </button>
      </div>

      <div class="tooltip-content">
        <p class="tooltip-description">{{ currentStep.description }}</p>

        <div v-if="currentStep.tips" class="tooltip-tips">
          <ul>
            <li v-for="tip in currentStep.tips" :key="tip">{{ tip }}</li>
          </ul>
        </div>
      </div>

      <div class="tooltip-actions">
        <el-button
          v-if="currentStepIndex > 0"
          @click="previousStep"
          type="text"
        >
          Previous
        </el-button>

        <div class="action-buttons">
          <el-button @click="skipTour" type="text">Skip Tour</el-button>

          <el-button
            v-if="currentStepIndex < tourSteps.length - 1"
            @click="nextStep"
            type="primary"
          >
            Next
          </el-button>

          <el-button
            v-else
            @click="completeTour"
            type="primary"
          >
            Start Searching!
          </el-button>
        </div>
      </div>
    </div>

    <!-- Progress dots -->
    <div class="tour-progress">
      <div
        v-for="(step, index) in availableSteps"
        :key="step.id"
        class="progress-dot"
        :class="{
          'active': index === currentStepNumber,
          'completed': index < currentStepNumber
        }"
      ></div>
    </div>
  </div>
</template>

<script>
import { trackOnboardingStep } from '../../utils/onboarding';

export default {
  name: 'SearchTour',
  props: {
    isActive: {
      type: Boolean,
      default: false
    },
    autoStart: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      windowWidth: window.innerWidth,
      currentStepIndex: 0,
      highlightStyle: {},
      tooltipStyle: {},
      tooltipClass: '',

      tourSteps: [
        {
          id: 'search-welcome',
          title: 'Welcome to Search!',
          description: 'This is where you can find all available sports events and memberships. Let\'s show you how to make the most of it.',
          target: null,
          position: 'center'
        },
        {
          id: 'member-selection',
          title: 'Choose Your Member',
          description: 'Select which member profile to register. Switch between family members anytime.',
          target: '.team-members-board',
          position: 'bottom'
        },
        {
          id: 'search-bar',
          title: 'Search Everything',
          description: 'Find events by sport name, location, or postcode. Results appear as you type.',
          target: '.search-box',
          position: 'bottom'
        },
        {
          id: 'mobile-filters',
          title: 'Search Options',
          description: 'Tap these icons to filter by sport type, location, dates, and more.',
          target: '.search-option',
          position: 'bottom'
        },
        {
          id: 'personalise-section',
          title: 'Personalise',
          description: 'Answer these questions to get recommendations that match your interests.',
          target: '.personalise',
          position: 'bottom'
        },
        {
          id: 'filters-section',
          title: 'Filters',
          description: 'Expand this section to apply filters and refine your search results.',
          target: '.personalise-filters',
          position: 'bottom'
        },
        {
          id: 'results-grid',
          title: 'Browse Results',
          description: 'All available events appear here. Click any card to see details and register.',
          target: '.registration-card-container',
          position: 'bottom'
        },

      ]
    };
  },
  computed: {
    currentStep() {
      return this.tourSteps[this.currentStepIndex];
    },
    availableSteps() {
      // Filter out steps based on screen size only (for progress dots)
      return this.tourSteps.filter((step) => {
        // Mobile: skip filters-section and personalise-section
        if ((step.id === 'filters-section' || step.id === 'personalise-section') && window.innerWidth <= 768) {
          return false;
        }
        // Desktop: skip mobile-filters
        if (step.id === 'mobile-filters' && window.innerWidth > 768) {
          return false;
        }
        return true;
      });
    },
    currentStepNumber() {
      // Find the current step's position in available steps
      const { availableSteps } = this;
      const { currentStep } = this;
      return availableSteps.findIndex((step) => step.id === currentStep.id);
    }
  },
  watch: {
    isActive(newVal) {
      if (newVal) {
        this.startTour();
      } else {
        this.resetTour();
      }
    },
    currentStepIndex() {
      this.$nextTick(() => {
        this.updateHighlight();
      });
    }
  },
  mounted() {
    if (this.autoStart && this.isActive) {
      this.startTour();
    }

    // Listen for window resize to update highlight positions and window width
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    startTour() {
      // Scroll to top first
      window.scrollTo({ top: 0, behavior: 'smooth' });

      this.currentStepIndex = 0;
      document.body.style.overflow = 'hidden';

      // Wait for scroll to complete before starting tour
      setTimeout(() => {
        this.$nextTick(() => {
          this.skipUnavailableSteps();
          this.updateHighlight();
        });
      }, 300);
    },

    resetTour() {
      this.currentStepIndex = 0;
      document.body.style.overflow = '';
      this.highlightStyle = {};
      this.tooltipStyle = {};
    },

    nextStep() {
      if (this.currentStepIndex < this.tourSteps.length - 1) {
        const { currentStep } = this;
        trackOnboardingStep(`search_${currentStep.id}`, 'step_completed');
        this.currentStepIndex++;
        this.skipUnavailableSteps();
      }
    },

    previousStep() {
      if (this.currentStepIndex > 0) {
        trackOnboardingStep(`search_${this.currentStep.id}`, 'step_back');
        this.currentStepIndex--;
        this.skipUnavailableSteps('backward');
      }
    },

    goToStep(index) {
      if (index >= 0 && index < this.tourSteps.length) {
        trackOnboardingStep(`search_${this.currentStep.id}`, 'step_jumped');
        this.currentStepIndex = index;
      }
    },

    skipTour() {
      trackOnboardingStep('search_tour', 'skipped');
      this.$emit('tour-skipped');
      this.closeTour();
    },

    completeTour() {
      trackOnboardingStep('search_tour', 'completed');
      this.$emit('tour-completed');
      this.closeTour();
    },

    closeTour() {
      this.$emit('tour-closed');
      document.body.style.overflow = '';
    },

    handleBackdropClick() {
      // Optional: close tour on backdrop click or do nothing
      // this.closeTour();
    },

    skipUnavailableSteps(direction = 'forward') {
      // Check if current step's target element exists
      const step = this.tourSteps[this.currentStepIndex];

      if (!step.target) {
        // No target means it's always available (like welcome step)
        return;
      }

      let targetSelector = step.target;
      // Update target for member board based on screen size (matching NewFinder.vue breakpoint)
      if (step.id === 'member-selection') {
        targetSelector = window.innerWidth <= 769 ? '.member-board-small-screensize' : '.team-members-board';
      }

      // Skip mobile-filters step on desktop
      if (step.id === 'mobile-filters' && window.innerWidth > 768) {
        console.log('Skipping mobile-filters on desktop');
        if (direction === 'forward') {
          if (this.currentStepIndex < this.tourSteps.length - 1) {
            this.currentStepIndex++;
            this.skipUnavailableSteps(direction);
          }
        } else if (this.currentStepIndex > 0) {
          this.currentStepIndex--;
          this.skipUnavailableSteps(direction);
        }
        return;
      }

      // Skip filters and personalise steps on mobile (they're replaced by mobile-filters)
      if ((step.id === 'filters-section' || step.id === 'personalise-section') && window.innerWidth <= 768) {
        console.log(`Skipping ${step.id} on mobile`);
        if (direction === 'forward') {
          if (this.currentStepIndex < this.tourSteps.length - 1) {
            this.currentStepIndex++;
            this.skipUnavailableSteps(direction);
          }
        } else if (this.currentStepIndex > 0) {
          this.currentStepIndex--;
          this.skipUnavailableSteps(direction);
        }
        return;
      }

      const element = document.querySelector(targetSelector);

      // Check if element is available and has visible content
      // Note: offsetParent is null for fixed/sticky positioned elements, so we need additional checks
      const isAvailable = element && (
        element.offsetParent !== null
        || (element.offsetWidth > 0 && element.offsetHeight > 0
         && (window.getComputedStyle(element).position === 'fixed'
          || window.getComputedStyle(element).position === 'sticky'))
      );

      // If element doesn't exist, is hidden, or has no content, skip this step
      if (!isAvailable) {
        console.log(`Skipping step "${step.id}" - target not available`, targetSelector, element);

        if (direction === 'forward') {
          if (this.currentStepIndex < this.tourSteps.length - 1) {
            this.currentStepIndex++;
            this.skipUnavailableSteps(direction); // Recursively check next step
          }
        } else if (this.currentStepIndex > 0) {
          this.currentStepIndex--;
          this.skipUnavailableSteps(direction); // Recursively check previous step
        }
      }
    },

    scrollElementIntoView(element) {
      if (!element || !(element instanceof Element)) {
        console.warn('scrollElementIntoView called with invalid element:', element);
        return;
      }
      if (typeof element.getBoundingClientRect !== 'function') return;

      const rect = element.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const tooltipHeight = 350; // Approximate tooltip height with padding
      const step = this.currentStep;

      // All tooltips positioned at bottom for consistency
      const tooltipPosition = step.position === 'center' ? 'center' : 'bottom';
      const isMobile = viewportWidth <= 768;

      // For center position (welcome step), no scrolling needed
      if (tooltipPosition === 'center') {
        return;
      }

      // Calculate space needed for tooltip below the element
      const bottomSpaceNeeded = tooltipHeight + 100; // Tooltip height + padding

      // Check if element and tooltip would both be visible
      const elementTopVisible = rect.top >= 0;
      const elementBottomVisible = rect.bottom + bottomSpaceNeeded <= viewportHeight;
      const isFullyVisible = elementTopVisible && elementBottomVisible && rect.left >= 0 && rect.right <= viewportWidth;

      if (!isFullyVisible) {
        // Scroll to position element near top of viewport with tooltip appearing below
        const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
        const elementHeight = rect.height;
        let scrollTo;

        if (isMobile) {
          // On mobile: account for sticky elements + ensure space for tooltip below
          // Position so element + tooltip both fit in viewport
          const stickyHeaderOffset = 120; // Nav (50px) + member board (~70px)
          const spaceForTooltip = 320; // Tooltip height + offset + padding
          const availableHeight = viewportHeight - stickyHeaderOffset;

          // If element + tooltip fit in available height, position at top
          if (elementHeight + spaceForTooltip <= availableHeight) {
            scrollTo = elementTop - stickyHeaderOffset;
          } else {
            // Element is too tall, position so tooltip is just visible at bottom
            scrollTo = elementTop - (viewportHeight - elementHeight - spaceForTooltip);
          }
        } else {
          // On desktop: position with some top padding
          scrollTo = elementTop - 100;
        }

        const targetTop = Math.max(0, scrollTo);
        const fallbackScroll = () => {
          try {
            if (typeof element.scrollIntoView === 'function') {
              element.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'center'
              });
              return;
            }
          } catch (error) {
            console.warn('scrollIntoView fallback failed, forcing manual scroll', error);
          }
          window.scrollTo(0, targetTop);
        };

        try {
          window.scrollTo({
            top: targetTop, // Don't scroll past the top
            behavior: 'smooth'
          });
        } catch (error) {
          console.warn('window.scrollTo failed, attempting fallback scroll strategy', error);
          fallbackScroll();
        }

        // Wait for scroll to complete before updating highlight
        setTimeout(() => {
          this.updateHighlightPosition();
        }, 500);
      }
    },

    updateHighlightPosition() {
      // This method updates just the highlight position without scrolling
      const step = this.currentStep;
      if (!step.target) return;

      let targetSelector = step.target;
      if (step.id === 'member-selection') {
        targetSelector = window.innerWidth <= 769 ? '.member-board-small-screensize' : '.team-members-board';
      }

      const element = document.querySelector(targetSelector);
      if (!element || typeof element.getBoundingClientRect !== 'function') return;

      const rect = element.getBoundingClientRect();
      const padding = 8;

      this.highlightStyle = {
        position: 'fixed',
        top: `${rect.top - padding}px`,
        left: `${rect.left - padding}px`,
        width: `${rect.width + (padding * 2)}px`,
        height: `${rect.height + (padding * 2)}px`,
        zIndex: 10001
      };
    },

    handleResize() {
      this.windowWidth = window.innerWidth;
      this.updateHighlight();
    },

    updateHighlight() {
      const step = this.currentStep;

      if (!step.target) {
        // Center the tooltip for steps without targets
        this.highlightStyle = {};
        this.tooltipStyle = {
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 10002
        };
        this.tooltipClass = 'tooltip-center';
        return;
      }

      // Update target based on current screen size for member board step (matching NewFinder.vue breakpoint)
      let targetSelector = step.target;
      if (step.id === 'member-selection') {
        targetSelector = window.innerWidth <= 769 ? '.member-board-small-screensize' : '.team-members-board';
      }

      const element = document.querySelector(targetSelector);
      if (!element) {
        console.warn(`Search tour target not found: ${targetSelector}`);

        // Fallback to center tooltip
        this.highlightStyle = {};
        this.tooltipStyle = {
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 10002
        };
        return;
      }

      // Scroll element into view if needed
      this.scrollElementIntoView(element);

      this.highlightElement(element, step);
    },

    highlightElement(element) {
      if (!element || typeof element.getBoundingClientRect !== 'function') return;
      const rect = element.getBoundingClientRect();
      const padding = 8;

      // Highlight style
      this.highlightStyle = {
        position: 'fixed',
        top: `${rect.top - padding}px`,
        left: `${rect.left - padding}px`,
        width: `${rect.width + (padding * 2)}px`,
        height: `${rect.height + (padding * 2)}px`,
        zIndex: 10001
      };

      // Tooltip positioning with better viewport boundary detection
      const tooltipOffset = 20;
      const tooltipWidth = 500;
      const tooltipHeight = 300; // Approximate tooltip height
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const sidePadding = 20;
      const bottomPadding = 20;

      let tooltipTop;
      let tooltipLeft;

      // Pre-calculate safe boundaries
      const safeLeft = sidePadding + (tooltipWidth / 2);
      const safeRight = viewportWidth - sidePadding - (tooltipWidth / 2);

      // Position tooltip at bottom of element
      tooltipTop = rect.bottom + tooltipOffset;
      tooltipLeft = rect.left + (rect.width / 2);

      // Ensure tooltip doesn't go off the bottom of the screen
      const maxTooltipTop = viewportHeight - tooltipHeight - bottomPadding;
      if (tooltipTop > maxTooltipTop) {
        tooltipTop = maxTooltipTop;
      }

      // Ensure horizontal centering is within bounds
      tooltipLeft = Math.max(safeLeft, Math.min(safeRight, tooltipLeft));

      this.tooltipStyle = {
        position: 'fixed',
        top: `${tooltipTop}px`,
        left: `${tooltipLeft}px`,
        zIndex: 10002
      };

      this.tooltipClass = 'tooltip-bottom';
    }
  }
};
</script>

<style lang="scss" scoped>
.search-tour {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 10000;
  pointer-events: none;

  * {
    pointer-events: auto;
  }
}

.tour-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  z-index: 10000;
}

.tour-highlight {
  border: 3px solid var(--highlight-color, #409eff);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.6);
  transition: all 0.3s ease;
}

.tour-tooltip {
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  padding: 0;
  max-width: 500px;
  min-width: 400px;
  width: 500px;
  transition: all 0.3s ease;

  &.tooltip-center {
    transform: translate(-50%, -50%);
  }

  &.tooltip-top {
    transform: translate(-50%, -100%);
    margin-top: -10px;

    &::after {
      content: '';
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      border: 10px solid transparent;
      border-top-color: white;
    }
  }

  &.tooltip-bottom {
    transform: translateX(-50%);
    margin-top: 10px;

    &::before {
      content: '';
      position: absolute;
      bottom: 100%;
      left: 50%;
      transform: translateX(-50%);
      border: 10px solid transparent;
      border-bottom-color: white;
    }
  }

  &.tooltip-left {
    transform: translate(-100%, -50%);
    margin-left: -10px;

    &::after {
      content: '';
      position: absolute;
      left: 100%;
      top: 50%;
      transform: translateY(-50%);
      border: 10px solid transparent;
      border-left-color: white;
    }
  }

  &.tooltip-right {
    transform: translateY(-50%);
    margin-left: 10px;

    &::before {
      content: '';
      position: absolute;
      right: 100%;
      top: 50%;
      transform: translateY(-50%);
      border: 10px solid transparent;
      border-right-color: white;
    }
  }
}

.tooltip-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f0f0f0;

  .tooltip-title {
    font-size: 1.3rem;
    font-weight: 600;
    color: #333;
    margin: 0;
    flex: 1;
  }

  .close-button {
    background: none;
    border: none;
    color: #999;
    cursor: pointer;
    padding: 0.25rem;
    border-radius: 4px;
    transition: all 0.2s ease;
    flex-shrink: 0;
    margin-left: 1rem;

    &:hover {
      background: #f5f5f5;
      color: #666;
    }
  }
}

.tooltip-content {
  padding: 1rem 1.5rem;
  max-height: 300px;
  overflow-y: auto;

  .tooltip-description {
    font-size: 0.95rem;
    line-height: 1.6;
    color: #666;
    margin: 0 0 0.75rem 0;
  }

  .tooltip-tips {
    ul {
      margin: 0;
      padding-left: 1.2rem;

      li {
        font-size: 0.85rem;
        color: #777;
        margin-bottom: 0.35rem;
        line-height: 1.5;
      }
    }
  }
}

.tooltip-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-top: 1px solid #f0f0f0;

  > .el-button {
    // Previous button styling (when visible)
    align-self: center;
  }

  .action-buttons {
    display: flex;
    gap: 0.75rem;
    align-items: center;
  }
}

.tour-progress {
  position: fixed;
  bottom: 30px;
  left: 20px;
  display: flex;
  gap: 0.5rem;
  z-index: 10003;

  .progress-dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.4);
    cursor: pointer;
    transition: all 0.3s ease;

    &.active {
      background: var(--highlight-color, #409eff);
      transform: scale(1.2);
    }

    &.completed {
      background: rgba(255, 255, 255, 0.8);
    }

    &:hover {
      background: var(--highlight-color, #409eff);
      opacity: 0.8;
    }
  }
}

// Mobile responsive
@media (max-width: 768px) {
  .tour-tooltip {
    max-width: calc(100vw - 2rem);
    min-width: calc(100vw - 2rem);
    width: calc(100vw - 2rem);
    margin: 1rem;

    &.tooltip-top {
      left: 50% !important;
      transform: translateX(-50%) !important;
      // Ensure it stays above the element
      margin-bottom: 1rem;
    }

    &.tooltip-bottom {
      left: 50% !important;
      transform: translateX(-50%) !important;
      margin-top: 1rem;
    }

    &.tooltip-left,
    &.tooltip-right {
      left: 50% !important;
      top: 50% !important;
      transform: translate(-50%, -50%) !important;
      margin: 1rem;
    }

    &.tooltip-center {
      left: 50% !important;
      top: 50% !important;
      transform: translate(-50%, -50%) !important;
      margin: 1rem;
      max-height: calc(100vh - 2rem);
      overflow-y: auto;
    }
  }

  .tooltip-header {
    padding: 0.75rem 1rem;

    .tooltip-title {
      font-size: 1.1rem;
    }
  }

  .tooltip-content {
    padding: 0.75rem 1rem;

    .tooltip-description {
      font-size: 0.95rem;
    }
  }

  .tooltip-actions {
    padding: 0.75rem 1rem;
    flex-direction: row;
    gap: 0.5rem;

    > .el-button {
      flex-shrink: 0;
    }

    .action-buttons {
      flex: 1;
      justify-content: flex-end;
    }
  }
}
</style>
